import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi } from '../../context';
import { Confirm } from '../../bits';
import { useLocale, useT } from '../../../messages';
import type { BrandingRead, BrandingRolledBack } from '../../../wire';
import { SiteDialog } from '../site/SiteDialog';
import { personOf, shortDate } from '../site/words';

/**
 * **Versions** — what is live now, and the kept versions it can go back to.
 *
 * Restoring is the existing rollback: the kept document published again as a
 * new, higher version, so it asks once more, in words — every guest sees it
 * on their next page load, and the unpublished changes in the builder are
 * replaced by it (the draft on this computer goes with them).
 */
export function VersionsDialog({
  tenantId,
  read,
  unpublished,
  onRestored,
  onClose,
}: {
  tenantId: string;
  read: BrandingRead;
  /** Whether the builder holds changes a restore would replace. */
  unpublished: boolean;
  onRestored: (answer: BrandingRolledBack) => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const action = useAction();
  const [version, setVersion] = useState<number | null>(null);

  const restore = async (): Promise<void> => {
    if (version === null) return;
    let answer: BrandingRolledBack | null = null;
    const ok = await action.run(async () => {
      answer = await api.rollbackBranding(tenantId, version);
    });
    if (ok && answer !== null) onRestored(answer);
  };

  if (version !== null) {
    return (
      <Confirm
        tone="primary"
        title={t('site.restore.title', { version })}
        consequence={`${t('site.restore.consequence', { version })}${
          unpublished ? ` ${t('builder.versions.replacesDraft')}` : ''
        }`}
        confirmLabel={t('site.restore.label', { version })}
        busy={action.busy}
        failure={action.failure}
        onConfirm={() => void restore()}
        onCancel={() => {
          action.clear();
          setVersion(null);
        }}
      />
    );
  }

  const published = read.branding_version > 0;
  return (
    <SiteDialog title={t('builder.versions.title')} onClose={onClose}>
      {published ? (
        <dl className="site-kv">
          <dt>{t('builder.versions.live')}</dt>
          <dd>
            {t('site.restore.version', { version: read.branding_version })} ·{' '}
            {t('site.page.whenBy', {
              day: shortDate(read.published_at, locale),
              who: personOf(read.published_by) ?? t('site.page.someone'),
            })}
          </dd>
          {read.publish_note === '' ? null : (
            <>
              <dt>{t('site.page.note')}</dt>
              <dd>“{read.publish_note}”</dd>
            </>
          )}
        </dl>
      ) : (
        <p className="muted">{t('site.page.never')}</p>
      )}

      {read.history.length === 0 ? (
        <p className="bld-hint">
          {t('branding.historyEmpty', { kept: read.contract.limits.history })}
        </p>
      ) : (
        <ul className="site-versions">
          {read.history.map((entry) => (
            <li key={entry.version}>
              <div>
                <strong>{t('site.restore.version', { version: entry.version })}</strong>{' '}
                <span className="muted">
                  {t('site.page.whenBy', {
                    day: shortDate(entry.at, locale),
                    who: personOf(entry.by) ?? t('site.page.someone'),
                  })}
                </span>
                {entry.note === '' ? null : <div className="muted">“{entry.note}”</div>}
              </div>
              <button
                type="button"
                className="button button--sm"
                aria-label={t('site.restore.label', { version: entry.version })}
                onClick={() => setVersion(entry.version)}
              >
                {t('site.restore.choose')}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="modal__actions">
        <button type="button" className="button" onClick={onClose}>
          {t('builder.close')}
        </button>
      </div>
    </SiteDialog>
  );
}
